import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useState, useEffect } from 'react';
import { Navigation, Pagination, Keyboard, A11y } from 'swiper';
import InsightCard from './cards/InsightCard';
import ButtonLink from '../shared/ButtonLinkComponent';
import useMediaQuery from '../custom-hooks/useMediaQuery';
import { withWidgetSpacingWrapper } from '../shared/WidgetSpacingWrapper';
import WidgetIdWrapper from '../shared/WidgetIdWrapper';
import { device } from '../../utils/styled-component-mixins';
import { useLocalization } from '../custom-hooks/useLocalization';
import { withLocalization } from '../shared/Context/LocalizationContext';
import BdoSelectInputContainer from '../shared/BdoSelectInput/BdoSelectInputContainer';
import CardSwiperWrapperStyled from './styled-components/CardSwiperWrapperStyled';
import NoResultsCard from '../shared/NoResultsCard';
import useIsFirstRender from '../custom-hooks/use-is-first-render';
import getSelectedOptionKey from '../../utils/get-selected-option-key';
import 'swiper/css';
import 'swiper/css/pagination';
import { compose } from '../utils/compose';

const InsightsSectionWrapperStyled = styled.div`
  .container-padding-md {
    padding-left: 0;
    padding-right: 0;
  }

  .cta-wrapper {
    margin-block-start: 1em;
    margin-block-end: -1em;
  }

  .related-resources-cta {
    display: flex;
    justify-content: center;
    line-height: 2.4rem;
    margin-bottom: 1.6rem;
    margin-top: 1.6rem;
    position: absolute;
    width: 100%;

    .btn {
      bottom: 3rem;
    }

    @media ${device.desktop} {
      justify-content: flex-end;
      right: 0;
    }
  }

  .heading-wrapper {
    margin-bottom: 4rem;

    @media ${device.desktop} {
      margin-bottom: 3rem;
    }

    h2 {
      font-size: 3rem;
      font-weight: normal;
      line-height: 3.2rem;
      padding: 0 1.5rem;
      position: relative;
      text-align: center;
      z-index: 1;

      @media ${device.desktop} {
        font-size: 3.6rem;
        line-height: 4.8rem;
      }
    }

    .sub-heading {
      margin: 1.6rem auto 0 auto;
      max-width: 70rem;
      padding: 0 1.5rem;
      position: relative;
      text-align: center;
      z-index: 1;

      @media ${device.desktop} {
        font-size: 2rem;
        line-height: 3.2rem;
      }
    }
  }

  .select-container {
    align-items: center;
    display: flex;
    flex: 1 1 100%;
    flex-direction: row;
    justify-content: center;
    margin-bottom: 3rem;
    max-width: 100%;
    padding: 0 1rem 1rem 1rem;
    position: relative;
    z-index: 2;

    @media ${device.desktop} {
      justify-content: flex-start;
      margin-bottom: 0.8rem;
      padding: 0 7rem 0 7rem;
    }

    label {
      line-height: 2.4rem;
      padding: 0;

      @media ${device.desktop} {
        padding: 0.8rem 0 1.6rem 0;
      }
    }
  }

  .dropdown-content * {
    border-radius: 0 !important;
  }

  .swiper {
    .swiper-slide {
      box-shadow: none;

      @media ${device.desktop} {
        max-width: 25%;
      }

      > div {
        margin-bottom: 0;
        height: 100%;

        .insight-card-wrapper {
          height: 100%;
        }

        .insight-card {
          height: 100%;
          display: flex;
          flex-direction: column;

          .featured-card-content {
            flex: 1 1 auto;
            min-height: 31.2rem;

            .animation-wrapper {
              display: flex;
              flex-direction: column;
              justify-content: space-between;
              height: 100%;
            }
          }
        }
      }
    }

    article {
      box-shadow: var(--card-shadow);
      height: 100%;

      a {
        height: 100%;
        justify-content: space-between;

        @media ${device.desktop} {
          min-height: 36rem;
        }
      }
    }
  }

  .no-results-wrapper {
    .no-results-card {
      min-height: 64.5rem;
      width: 43.7rem;
    }
  }

  .no-results-wrapper {
    .no-results-card {
      min-height: 64.5rem;
      width: 43.7rem;
    }
  }
`;

const InsightsSectionComponent = (props) => {
  let { resourceStrings } = useLocalization();
  resourceStrings = props.resourceStrings ? props.resourceStrings : resourceStrings;
  const dropDownLabel = resourceStrings['bdO.InsightsSectionWidget.FilterLabel'];
  const readMoreText = resourceStrings['bdO.FeaturedInsight.LinkText'];
  const noResultsMessage = resourceStrings['bdO.InsightSectionWidget.NoResults'];
  const featuredTagText = resourceStrings['bdO.FeaturedCard.FeaturedTagText'];
  const filterLabel = props.filterLabel
    ? props.filterLabel
    : resourceStrings['bdO.EventsWebinarsSliderWidget.FilterLabel'];
  const [filteredInsights, setFilteredInsights] = useState([]);
  const [featuredInsightsNodeIds, setFeaturedInsightsNodeIds] = useState([]);
  const [insightFilterValue, setInsightFilterValue] = useState(props.filterOptions[0]);
  const isDesktop = useMediaQuery(device.desktop);
  const isFirstRender = useIsFirstRender();
  const cardProps = {
    layout: 'standard',
    mediaSide: 'left',
    featuredTagText
  };
  const { selectedFilter, selectedIndustry } = props;

  const dropDownMap = (categories, firstItem) => {
    const options = Object.keys(categories).map((key) => categories[key]);

    return [firstItem, ...options];
  };

  const getPreselectedFilter = () => {
    const filterKeys = Object.keys(props.filterOptions);
    let preselectedFilter = null;

    filterKeys.forEach((key, index) => {
      if (key?.toLowerCase() === selectedIndustry?.toLowerCase()) {
        // add 1 to index to account for the first item in the dropdown
        preselectedFilter = index + 1;
      }
    });

    return preselectedFilter;
  };

  const applyInsightFilter = (filterValue) => {
    const featuredInsights = featuredInsightsNodeIds.join(',');
    const filterKey =
      filterValue === dropDownLabel ? '' : getSelectedOptionKey(props.filterOptions, filterValue);

    fetch(
      `/api/insights?category=${selectedFilter}&insightIndustry=${filterKey}&fetchAmount=16&excludeTotal=true&featured=${featuredInsights}&initialIndustry=${props.initialIndustry}`
    )
      .then((response) => response.json())
      .then((data) => {
        setFilteredInsights(data);
      });
  };

  useEffect(() => {
    if (!isFirstRender) {
      applyInsightFilter(insightFilterValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insightFilterValue]);

  useEffect(() => {
    setFilteredInsights(props.items);
  }, [props.items]);

  useEffect(() => {
    const nodeIds = props.featuredItems.map((item) => item.nodeId);
    setFeaturedInsightsNodeIds(nodeIds);
  }, [props.featuredItems]);
  return (
    <WidgetIdWrapper widgetID={props.properties.widgetID}>
      <div data-ktc-search-exclude>
        <InsightsSectionWrapperStyled>
          {(props.properties.headerText || props.properties.subHeaderText) && (
            <div className="heading-wrapper">
              {props.properties.headerText && <h2>{props.properties.headerText}</h2>}
              {props.properties.subHeaderText && (
                <p className="sub-heading">{props.properties.subHeaderText}</p>
              )}
            </div>
          )}
          <BdoSelectInputContainer
            className="bdo-select-container"
            filterLabel={filterLabel}
            filters={[
              {
                filterOptions: dropDownMap(props.filterOptions, dropDownLabel),
                onChange: setInsightFilterValue,
                preselectedOption: getPreselectedFilter()
              }
            ]}
          />
          <CardSwiperWrapperStyled backgroundImage={props.properties.backgroundImage}>
            <div className="container-padding-md">
              <div className="card-slider">
                <Swiper
                  observer={true}
                  pagination={{ clickable: { isDesktop } }}
                  modules={[Pagination, Navigation, Keyboard, A11y]}
                  a11y={true}
                  navigation={true}
                  keyboard={true}
                  breakpoints={{
                    320: {
                      centeredSlides: true,
                      slidesPerView: 'auto',
                      slidesPerGroup: 1,
                      spaceBetween: 16
                    },
                    992: {
                      centeredSlides: false,
                      slidesPerView: 4,
                      slidesPerGroup: 4,
                      spaceBetween: 16
                    },
                    1921: {
                      centeredSlides: false,
                      slidesPerGroup: 5,
                      slidesPerView: 5,
                      spaceBetween: 16
                    }
                  }}
                >
                  {filteredInsights.length > 0 ? (
                    filteredInsights.map((item) => (
                      <SwiperSlide key={item.nodeId}>
                        <InsightCard
                          cardProps={cardProps}
                          item={{
                            thumbnail1: item.image,
                            primaryCategory: item.primaryCategory,
                            pageTitle: item.pageTitle,
                            publishDate: item.publishDate,
                            readMoreText,
                            description: item.description,
                            url: item.url,
                            externalUrl: item.externalUrl,
                            isFeatured: item.isFeatured,
                            isExternal: item.isExternal
                          }}
                        />
                      </SwiperSlide>
                    ))
                  ) : (
                    <NoResultsCard noResultsMessage={noResultsMessage} />
                  )}
                </Swiper>
                {props.properties.ctaText && props.properties.ctaURL && (
                  <div className="related-resources-cta">
                    <ButtonLink
                      text={props.properties.ctaText}
                      link={props.properties.ctaURL}
                      type="tertiary"
                      theme="light"
                    />
                  </div>
                )}
              </div>
            </div>
          </CardSwiperWrapperStyled>
        </InsightsSectionWrapperStyled>
      </div>
    </WidgetIdWrapper>
  );
};

export default compose(withLocalization, withWidgetSpacingWrapper)(InsightsSectionComponent);
